import { flexRender, type RowData } from '@tanstack/react-table';
import { type LegacyHeaderGroup as HeaderGroup } from '@tanstack/react-table/legacy';
import { Filter } from '../Filter';
import { TableHeaderSorting } from '../TableHeaderSorting';
import { TableHeaderTooltip } from '../TableHeaderTooltip';
import {
  StyledTableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableHeaderCellInner,
  HeaderNameWrapper,
} from './styles';

type TableHeaderProps<TData extends RowData> = {
  headerGroups: HeaderGroup<TData>[];
  sorting: boolean;
  allowFiltering?: boolean;
  stickyHeader?: boolean;
};

export const TableHeader = <TData extends RowData>({
  headerGroups,
  sorting,
  allowFiltering,
  stickyHeader,
}: TableHeaderProps<TData>) => {
  return (
    <StyledTableHeader sticky={stickyHeader}>
      {headerGroups.map((headerGroup) => {
        return (
          <TableHeaderRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <TableHeaderCell
                  key={header.id}
                  slim={header.column.columnDef.meta?.slim}
                  colSpan={header.colSpan}
                  data-testid={`table-header-cell-${header.id}`}
                >
                  <TableHeaderCellInner align={header.column.columnDef.meta?.align}>
                    {header.isPlaceholder ? null : (
                      <>
                        <TableHeaderSorting
                          align={header.column.columnDef.meta?.align}
                          isSorted={header.column.getIsSorted()}
                          isSortable={header.column.getCanSort() && sorting}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <HeaderNameWrapper align={header.column.columnDef.meta?.align}>
                            <span>
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </span>
                            <TableHeaderTooltip
                              tooltip={header.column.columnDef.meta?.tooltip}
                              customTooltip={header.column.columnDef.meta?.customTooltip}
                              context={header.getContext()}
                            />
                          </HeaderNameWrapper>
                        </TableHeaderSorting>
                        {allowFiltering && header.column.getCanFilter() && (
                          <Filter column={header.column} />
                        )}
                      </>
                    )}
                  </TableHeaderCellInner>
                </TableHeaderCell>
              );
            })}
          </TableHeaderRow>
        );
      })}
    </StyledTableHeader>
  );
};
